import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { carrierApi, monitoringApi, vinApi, scoringApi } from '../services/api';
import { useCarrierData } from '../hooks/useCarrierData';
import { logger } from '../utils/logger';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { Navbar } from '../components/shared/Navbar';
import { VINDetailModal } from '../components/VINDetailModal';
import { ShellGameTimelineModal } from '../components/ShellGameTimelineModal';
import { InspectionDetailsModal } from '../components/shared/InspectionDetailsModal';
import CarrierChangeTimeline from '../components/CarrierChangeTimeline';
import { CarrierSearch } from '../components/carrier/CarrierSearch';
import { CarrierProfile } from '../components/carrier/CarrierProfile';
import { CarrierTabs } from '../components/carrier/CarrierTabs';
import { FairScoreComparison } from '../components/carrier/FairScoreComparison';
import { Search, Truck, Phone, Mail, User, Hash, MapPinned, History, TrendingUp, XCircle, AlertTriangle } from 'lucide-react';
import type { CarrierProfile as CarrierProfileType, BASICScore } from '../types';
import type { Inspection, Violation } from '../types/api';
import '../styles/modern.css';

// Props interface for CarrierDashboard
interface CarrierDashboardProps {
  dotNumberOverride?: number;
  onBackToSearch?: () => void;
}

/**
 * Calculate health score - uses backend API with fallback to client-side calculation
 * Backend API: GET /api/scoring/health/:dotNumber?simple=true
 */
const calculateHealthScoreLocal = (profile: CarrierProfileType): number => {
  let score = 100;

  // Safety rating penalties
  if (profile.safetyRating === 'U') score -= 30;
  else if (profile.safetyRating === 'C') score -= 15;

  // Recent violations and crashes (past 6 months)
  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

  // Authority status penalties (note: these fields may not exist on API type)
  const authorityStatus = (profile as CarrierProfileType & { authorityStatus?: string }).authorityStatus;
  if (authorityStatus === 'Revoked') score -= 40;
  else if (authorityStatus === 'Suspended') score -= 25;

  // Out of service penalties
  const outOfServiceDate = (profile as CarrierProfileType & { outOfServiceDate?: string }).outOfServiceDate;
  if (outOfServiceDate) score -= 20;

  return Math.max(0, Math.min(100, score));
};

const calculateHealthScore = calculateHealthScoreLocal;

const getRiskLevel = (score: number): { level: string; color: string } => {
  if (score >= 80) return { level: 'LOW', color: '#10b981' };
  if (score >= 60) return { level: 'MEDIUM', color: '#f59e0b' };
  if (score >= 40) return { level: 'HIGH', color: '#ef4444' };
  return { level: 'CRITICAL', color: '#dc2626' };
};

export const CarrierDashboard: React.FC<CarrierDashboardProps> = ({ dotNumberOverride, onBackToSearch }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { dotNumber: urlDotNumber } = useParams<{ dotNumber?: string }>();
  const [dotNumber, setDotNumber] = useState('');
  const [searchedDot, setSearchedDot] = useState<number | null>(dotNumberOverride || null);

  // Use carrier data hook for all data loading
  const carrierData = useCarrierData();
  const {
    profile,
    inspections,
    crashes,
    violations,
    vehicles,
    censusData,
    observedUnits,
    contactMatches,
    changeHistory,
    historicStats,
    basics,
    shellGameAlerts,
    loading,
    error: carrierError,
    loadingMore,
    loadCarrier,
    loadMoreInspections,
    loadMoreViolations,
    loadMoreCrashes,
    totalInspections,
    totalViolations,
    totalCrashes,
    checkingShellGame,
    hasCheckedShellGame,
    checkShellGame,
    loadChangeHistory,
  } = carrierData;

  // Local UI state
  const error = carrierError;

  // UI states for modals and selections
  const [selectedInspection, setSelectedInspection] = useState<Inspection | null>(null);
  const [inspectionViolations, setInspectionViolations] = useState<Violation[]>([]);
  const [loadingViolations, setLoadingViolations] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [selectedVin, setSelectedVin] = useState<string | null>(null);
  const [timelineVin, setTimelineVin] = useState<string | null>(null);

  // Monitoring states
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [addingToWatchlist, setAddingToWatchlist] = useState(false);

  // Modal states
  const [showContactMatchesModal, setShowContactMatchesModal] = useState(false);
  const [showChangeHistoryModal, setShowChangeHistoryModal] = useState(false);

  // Health score state (fetched from backend API)
  const [backendHealthScore, setBackendHealthScore] = useState<{ score: number; riskLevel: string } | null>(null);

  // Fetch health score from backend API
  useEffect(() => {
    const fetchHealthScore = async () => {
      if (!searchedDot) {
        setBackendHealthScore(null);
        return;
      }
      try {
        const response = await scoringApi.getHealthScore(searchedDot, true);
        // API returns SimpleHealthScore { score, riskLevel?, dotNumber? }
        if (response && typeof response.score === 'number') {
          setBackendHealthScore({
            score: response.score,
            riskLevel: response.riskLevel || getRiskLevel(response.score).level
          });
        }
      } catch (err: any) {
        // Fall back to local calculation if API fails
        logger.warn('Failed to fetch health score from API, using local calculation', err);
        setBackendHealthScore(null);
      }
    };
    fetchHealthScore();
  }, [searchedDot]);

  // Calculate 6-month filtered counts for dashboard cards
  const sixMonthCounts = useMemo(() => {
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

    const recentCrashes = crashes.filter(crash => {
      // API returns camelCase: reportDate
      const dateField = (crash as any).reportDate || (crash as any).reportDate;
      if (!dateField) return false;
      const crashDate = new Date(dateField as string);
      return crashDate >= sixMonthsAgo;
    });

    const recentInspections = inspections.filter(insp => {
      // API returns camelCase: inspDate
      const dateField = (insp as any).inspDate || (insp as any).inspDate;
      if (!dateField) return false;
      const inspDate = new Date(dateField as string);
      return inspDate >= sixMonthsAgo;
    });

    const recentInspectionIds = new Set(
      // API returns camelCase: inspectionId
      recentInspections.map(insp => (insp as any).inspectionId || (insp as any).inspectionId)
    );

    const recentViolations = violations.filter(violation =>
      // API returns camelCase: inspectionId
      recentInspectionIds.has((violation as any).inspectionId || (violation as any).inspectionId)
    );

    const failedInspections = recentInspections.filter(insp => {
      // API returns camelCase: violTotal
      const violCount = Number((insp as any).violTotal || 0);
      return violCount > 0;
    });

    const cleanInspections = recentInspections.filter(insp => {
      // API returns camelCase: violTotal
      const violCount = Number((insp as any).violTotal || 0);
      return violCount === 0;
    });

    return {
      crashes: recentCrashes.length,
      violations: recentViolations.length,
      inspections: {
        total: recentInspections.length,
        failed: failedInspections.length,
        clean: cleanInspections.length
      }
    };
  }, [crashes, violations, inspections]);

  // Fetch violations when an inspection is selected
  useEffect(() => {
    const fetchInspectionViolations = async () => {
      if (!(selectedInspection as any)?.inspectionId) {
        setInspectionViolations([]);
        return;
      }

      setLoadingViolations(true);
      try {
        const data = await vinApi.getInspectionViolations((selectedInspection as any).inspectionId);
        setInspectionViolations(data || []);
      } catch (error: any) {
        logger.error('Error fetching inspection violations', error);
        setInspectionViolations([]);
      } finally {
        setLoadingViolations(false);
      }
    };

    fetchInspectionViolations();
  }, [selectedInspection]);

  // Handle dotNumberOverride prop
  useEffect(() => {
    if (dotNumberOverride && !profile) {
      setSearchedDot(dotNumberOverride);
      setDotNumber(dotNumberOverride.toString());
      loadCarrier(dotNumberOverride);
    }
  }, [dotNumberOverride, profile, loadCarrier]);

  // Check for DOT number in URL params or query string on mount
  useEffect(() => {
    if (dotNumberOverride) return;

    if (urlDotNumber && !searchedDot) {
      const dotNum = parseInt(urlDotNumber, 10);
      if (!isNaN(dotNum)) {
        setDotNumber(urlDotNumber);
        setSearchedDot(dotNum);
        loadCarrier(dotNum);
        return;
      }
    }

    const urlDot = searchParams.get('dot');
    if (urlDot && !searchedDot) {
      const dotNum = parseInt(urlDot, 10);
      if (!isNaN(dotNum)) {
        setDotNumber(urlDot);
        setSearchedDot(dotNum);
        loadCarrier(dotNum);
      }
    }
  }, [searchParams, dotNumberOverride, urlDotNumber, searchedDot, loadCarrier]);

  // Handle carrier selection from search component
  const handleCarrierSelect = (selectedDot: number) => {
    console.log('[CarrierDashboard] handleCarrierSelect called with DOT:', selectedDot);
    setSearchedDot(selectedDot);
    console.log('[CarrierDashboard] Calling loadCarrier with DOT:', selectedDot);
    loadCarrier(selectedDot);
    // Navigate to carrier profile URL so refresh works properly
    console.log('[CarrierDashboard] Navigating to /carrier/' + selectedDot);
    navigate(`/carrier/${selectedDot}`);
    console.log('[CarrierDashboard] Navigation complete');
  };

  // Add to watchlist function
  const handleAddToWatchlist = async () => {
    if (!searchedDot) return;

    try {
      setAddingToWatchlist(true);
      await monitoringApi.addCarrierToWatchlist(searchedDot);
      setIsMonitoring(true);
      alert(`${profile?.legalName} added to your watchlist! You'll receive alerts for any changes.`);
    } catch (err: any) {
      logger.error('Error adding to watchlist:', err);
      const isConflict = err && typeof err === 'object' && 'response' in err
        ? (err as any).response?.data?.error === 'Conflict'
        : false;

      if (isConflict) {
        alert('This carrier is already in your watchlist');
      } else {
        const errorMessage = err && typeof err === 'object' && 'response' in err
          ? (err as any).response?.data?.message
          : err instanceof Error ? err.message : 'Unknown error';
        alert('Failed to add carrier to watchlist: ' + String(errorMessage));
      }
    } finally {
      setAddingToWatchlist(false);
    }
  };

  // Calculate health score and risk level (prefer backend API score over local calculation)
  const healthScore = backendHealthScore?.score ?? (profile ? calculateHealthScore(profile) : 0);
  const riskLevel = backendHealthScore?.riskLevel
    ? { level: backendHealthScore.riskLevel, color: getRiskLevel(healthScore).color }
    : getRiskLevel(healthScore);

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />

      {/* Back to Search Button */}
      {onBackToSearch && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-0">
          <button
            onClick={onBackToSearch}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-xs)',
              padding: 'var(--space-sm) var(--space-md)',
              background: 'white',
              color: 'var(--color-gray-700)',
              border: '1px solid var(--color-gray-300)',
              borderRadius: 'var(--radius-md)',
              fontWeight: '500',
              fontSize: '0.875rem',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--color-gray-50)';
              e.currentTarget.style.borderColor = 'var(--color-gray-400)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'white';
              e.currentTarget.style.borderColor = 'var(--color-gray-300)';
            }}
          >
            <Search size={16} />
            New Search
          </button>
        </div>
      )}

      {/* Search Section */}
      {!dotNumberOverride && (
        <CarrierSearch
          onCarrierSelect={handleCarrierSelect}
          loading={loading}
        />
      )}

      {/* Loading State */}
      {loading && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <LoadingSpinner message="Loading carrier intelligence..." size="lg" />
        </div>
      )}

      {/* Error State */}
      {!loading && searchedDot && error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 text-center">
            <div className="flex items-center justify-center mb-4">
              <AlertTriangle className="w-12 h-12 text-red-500" />
            </div>
            <h3 className="text-xl font-semibold text-red-900 mb-2">
              Unable to Load Carrier Data
            </h3>
            <p className="text-red-700 mb-4">
              {error || 'Failed to load carrier information. Please try again or contact support if the problem persists.'}
            </p>
            <button
              onClick={() => {
                setSearchedDot(null);
                setDotNumber('');
              }}
              className="px-6 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
            >
              Try Another Search
            </button>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && !searchedDot && !error && (
        <div className="container-spacious">
          <div className="card-modern fade-in-up" style={{ textAlign: 'center', padding: 'var(--space-3xl)', animationDelay: '0.3s' }}>
            <div style={{
              width: '96px',
              height: '96px',
              background: 'var(--color-gray-100)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto var(--space-xl)'
            }}>
              <Truck style={{ width: '48px', height: '48px', color: 'var(--color-gray-300)' }} />
            </div>
            <h3 className="heading-2" style={{ marginBottom: 'var(--space-md)' }}>
              Start Your Intelligence Search
            </h3>
            <p className="text-muted" style={{ maxWidth: '600px', margin: '0 auto' }}>
              Enter a DOT number above to unlock comprehensive carrier intelligence with real-time analytics,
              VIN tracking, and violation analysis.
            </p>
          </div>
        </div>
      )}

      {/* Results Section */}
      {!loading && searchedDot && profile && (
        <div className="container-spacious">
          {/* Profile Header Component */}
          <CarrierProfile
            profile={profile}
            healthScore={healthScore}
            riskLevel={riskLevel}
            sixMonthCounts={sixMonthCounts}
            isMonitored={isMonitoring}
            onAddToWatchlist={handleAddToWatchlist}
            addingToWatchlist={addingToWatchlist}
          />

          {/* Fair Score Comparison Widget - FMCSA vs Our Bayesian Model */}
          <FairScoreComparison
            dotNumber={searchedDot}
            basics={basics}
          />

          {/* Contact Information Card - Census Data */}
          {censusData && (
            <div className="card-modern fade-in-up section-spacing" style={{ animationDelay: '0.1s' }}>
              <h3 className="heading-3" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-lg)' }}>
                <Phone style={{ color: 'var(--color-primary)' }} />
                Contact Information
              </h3>

              <div className="grid-spacious" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
                {/* Phone Number */}
                {censusData.telephone && (
                  <div style={{ display: 'flex', alignItems: 'start', gap: 'var(--space-md)' }}>
                    <Phone size={16} style={{ color: 'var(--color-gray-400)', marginTop: '4px' }} />
                    <div>
                      <div className="stat-label" style={{ marginBottom: 'var(--space-xs)' }}>PHONE NUMBER</div>
                      <a href={`tel:${censusData.telephone}`} style={{ color: 'var(--color-primary)', fontWeight: '500' }} className="hover:underline">
                        {String(censusData.telephone).replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')}
                      </a>
                    </div>
                  </div>
                )}

                {/* Email Address */}
                {censusData.emailAddress && (
                  <div style={{ display: 'flex', alignItems: 'start', gap: 'var(--space-md)' }}>
                    <Mail size={16} style={{ color: 'var(--color-gray-400)', marginTop: '4px' }} />
                    <div>
                      <div className="stat-label" style={{ marginBottom: 'var(--space-xs)' }}>EMAIL</div>
                      <a href={`mailto:${censusData.emailAddress}`} style={{ color: 'var(--color-primary)', fontWeight: '500', textTransform: 'uppercase' }} className="hover:underline">
                        {censusData.emailAddress}
                      </a>
                    </div>
                  </div>
                )}

                {/* Fax */}
                {censusData.fax && (
                  <div style={{ display: 'flex', alignItems: 'start', gap: 'var(--space-md)' }}>
                    <Phone size={16} style={{ color: 'var(--color-gray-400)', marginTop: '4px' }} />
                    <div>
                      <div className="stat-label" style={{ marginBottom: 'var(--space-xs)' }}>FAX</div>
                      <div style={{ color: 'var(--color-gray-900)', fontWeight: '500' }}>
                        {String(censusData.fax).replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')}
                      </div>
                    </div>
                  </div>
                )}

                {/* Company Officer */}
                {censusData.companyOfficer1 && (
                  <div style={{ display: 'flex', alignItems: 'start', gap: 'var(--space-md)' }}>
                    <User size={16} style={{ color: 'var(--color-gray-400)', marginTop: '4px' }} />
                    <div>
                      <div className="stat-label" style={{ marginBottom: 'var(--space-xs)' }}>COMPANY OFFICER</div>
                      <div style={{ color: 'var(--color-gray-900)', fontWeight: '700' }}>{censusData.companyOfficer1 as React.ReactNode}</div>
                    </div>
                  </div>
                )}

                {/* DUNS Number */}
                {censusData.dunsNumber && (
                  <div style={{ display: 'flex', alignItems: 'start', gap: 'var(--space-md)' }}>
                    <Hash size={16} style={{ color: 'var(--color-gray-400)', marginTop: '4px' }} />
                    <div>
                      <div className="stat-label" style={{ marginBottom: 'var(--space-xs)' }}>DUNS NUMBER</div>
                      <div style={{ color: 'var(--color-gray-900)', fontWeight: '700' }}>{censusData.dunsNumber as React.ReactNode}</div>
                    </div>
                  </div>
                )}

                {/* EIN */}
                {censusData.ein && (
                  <div style={{ display: 'flex', alignItems: 'start', gap: 'var(--space-md)' }}>
                    <Hash size={16} style={{ color: 'var(--color-gray-400)', marginTop: '4px' }} />
                    <div>
                      <div className="stat-label" style={{ marginBottom: 'var(--space-xs)' }}>EIN</div>
                      <div style={{ color: 'var(--color-gray-900)', fontWeight: '700' }}>{censusData.ein as React.ReactNode}</div>
                    </div>
                  </div>
                )}

                {/* Mailing Address */}
                {censusData.mailingAddress && ((censusData.mailingAddress as any).street || (censusData.mailingAddress as any).city) && (
                  <div style={{ display: 'flex', alignItems: 'start', gap: 'var(--space-md)' }}>
                    <MapPinned size={16} style={{ color: 'var(--color-gray-400)', marginTop: '4px' }} />
                    <div>
                      <div className="stat-label" style={{ marginBottom: 'var(--space-xs)' }}>MAILING ADDRESS</div>
                      <div style={{ color: 'var(--color-gray-900)', fontWeight: '500' }}>
                        {(censusData.mailingAddress as any).street && <div>{(censusData.mailingAddress as any).street}</div>}
                        <div>
                          {(censusData.mailingAddress as any).city && <span>{(censusData.mailingAddress as any).city}</span>}
                          {(censusData.mailingAddress as any).state && <span>, {(censusData.mailingAddress as any).state}</span>}
                          {(censusData.mailingAddress as any).zip && <span> {(censusData.mailingAddress as any).zip}</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Fleet Composition */}
              {censusData.fleet && (Number(censusData.fleet.trucks) > 0 || Number(censusData.fleet.tractors) > 0 || Number(censusData.fleet.trailers) > 0) && (
                <div style={{ marginTop: 'var(--space-xl)', paddingTop: 'var(--space-lg)', borderTop: '1px solid var(--color-gray-200)' }}>
                  <h4 className="heading-3" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-lg)', fontSize: '1rem' }}>
                    <Truck style={{ color: 'var(--color-primary)', size: 18 } as any} />
                    Fleet Composition
                  </h4>
                  <div className="grid-spacious" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
                    {Number(censusData.fleet.trucks) > 0 && (
                      <div className="stat-box">
                        <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--color-gray-900)' }}>{censusData.fleet.trucks}</div>
                        <div className="stat-label">TRUCKS</div>
                      </div>
                    )}
                    {Number(censusData.fleet.tractors) > 0 && (
                      <div className="stat-box">
                        <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--color-gray-900)' }}>{censusData.fleet.tractors}</div>
                        <div className="stat-label">TRACTORS</div>
                      </div>
                    )}
                    {Number(censusData.fleet.trailers) > 0 && (
                      <div className="stat-box">
                        <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--color-gray-900)' }}>{censusData.fleet.trailers}</div>
                        <div className="stat-label">TRAILERS</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* FMCSA Census Data History */}
              {historicStats && historicStats.currentCensus && (
                <div style={{ marginTop: 'var(--space-xl)', paddingTop: 'var(--space-lg)', borderTop: '1px solid var(--color-gray-200)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-lg)' }}>
                    <h4 className="heading-3" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', fontSize: '1rem', margin: 0 }}>
                      <TrendingUp size={18} style={{ color: 'var(--color-primary)' }} />
                      FMCSA Census Data History ({historicStats.totalSnapshots} MCS-150 Updates)
                    </h4>
                    <button
                      onClick={async () => {
                        logger.debug('Opening change history modal', { changeHistory, searchedDot });
                        if (!changeHistory) {
                          await loadChangeHistory();
                        }
                        setShowChangeHistoryModal(true);
                      }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 'var(--space-xs)',
                        padding: 'var(--space-sm) var(--space-md)',
                        background: 'var(--color-primary)',
                        color: 'white',
                        border: 'none',
                        borderRadius: 'var(--radius-md)',
                        fontWeight: '500',
                        fontSize: '0.875rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: 'var(--shadow-sm)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--color-primary-dark)';
                        e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'var(--color-primary)';
                        e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <History size={14} />
                      View Change History
                    </button>
                  </div>

                  <div style={{ display: 'flex', gap: 'var(--space-xl)', flexWrap: 'wrap', alignItems: 'center' }}>
                    {/* Earliest Census Data */}
                    {historicStats.oldestSnapshot && Number(historicStats.totalSnapshots) > 1 && (
                      <div style={{ flex: '1', minWidth: '200px' }}>
                        <div className="stat-label" style={{ marginBottom: 'var(--space-md)' }}>
                          EARLIEST CENSUS ({new Date((historicStats.oldestSnapshot as any).snapshot_date).toLocaleDateString()})
                        </div>
                        <div className="grid-spacious" style={{ gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--space-md)' }}>
                          <div className="stat-box">
                            <div style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--color-gray-900)' }}>
                              {(historicStats.oldestSnapshot as any).power_units || 0}
                            </div>
                            <div className="stat-label">Power Units</div>
                          </div>
                          <div className="stat-box">
                            <div style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--color-gray-900)' }}>
                              {(historicStats.oldestSnapshot as any).drivers || 0}
                            </div>
                            <div className="stat-label">Drivers</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Arrow if we have comparison data */}
                    {Number(historicStats.totalSnapshots) > 1 && (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', color: 'var(--color-gray-400)' }}>
                        →
                      </div>
                    )}

                    {/* Current Census Data */}
                    <div style={{ flex: '1', minWidth: '200px' }}>
                      <div className="stat-label" style={{ marginBottom: 'var(--space-md)' }}>
                        CURRENT MCS-150 ({new Date((historicStats.currentCensus as any).snapshot_date).toLocaleDateString()})
                      </div>
                      <div className="grid-spacious" style={{ gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--space-md)' }}>
                        <div className="stat-box">
                          <div style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--color-gray-900)' }}>
                            {(historicStats.currentCensus as any).power_units || 0}
                          </div>
                          <div className="stat-label">Power Units</div>
                        </div>
                        <div className="stat-box">
                          <div style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--color-gray-900)' }}>
                            {(historicStats.currentCensus as any).drivers || 0}
                          </div>
                          <div className="stat-label">Drivers</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Contact Matches Modal - Inline Display */}
          {contactMatches && showContactMatchesModal && (
            <div className="card-modern section-spacing fade-in-up" style={{ animationDelay: '0.08s' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 'var(--space-lg)' }}>
                <div>
                  <h2 className="heading-2">Comprehensive Carrier Matches</h2>
                  <p className="text-muted">Carriers sharing VINs, contact information, or multiple criteria with {profile?.legalName}</p>
                </div>
                <button onClick={() => setShowContactMatchesModal(false)} className="btn-icon">
                  <XCircle size={24} />
                </button>
              </div>

              {/* Summary */}
              <div className="card-modern" style={{ marginBottom: 'var(--space-lg)', background: '#fef3c7' }}>
                <div style={{ display: 'flex', gap: 'var(--space-md)', alignItems: 'center' }}>
                  <AlertTriangle size={24} style={{ color: '#f59e0b' }} />
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 'var(--space-xs)' }}>
                      {contactMatches.summary.totalMatches} Total Matches Found
                    </div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--color-gray-600)' }}>
                      {(Number(contactMatches.summary.priorityMatches) > 0 && (
                        <><strong>{contactMatches.summary.priorityMatches} CRITICAL</strong> (Multiple match types) • </>
                      )) as React.ReactNode}
                      {(Number(contactMatches.summary.vinMatches) > 0 && (
                        <>{contactMatches.summary.vinMatches} VIN matches • </>
                      )) as React.ReactNode}
                      {Number(contactMatches.summary.phoneMatches) + Number(contactMatches.summary.emailMatches) + Number(contactMatches.summary.officerMatches) + Number(contactMatches.summary.addressMatches)} Contact matches
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tabbed Interface */}
          <CarrierTabs
            dotNumber={searchedDot}
            profile={profile}
            inspections={inspections as any}
            violations={violations as any}
            crashes={crashes as any}
            vehicles={vehicles}
            basics={basics as any}
            censusData={censusData as any}
            observedUnits={observedUnits as any}
            historicStats={historicStats as any}
            totalInspections={totalInspections}
            totalViolations={totalViolations}
            totalCrashes={totalCrashes}
            loadingMore={loadingMore}
            onLoadMoreInspections={loadMoreInspections}
            onLoadMoreViolations={loadMoreViolations}
            onLoadMoreCrashes={loadMoreCrashes}
            contactMatches={contactMatches as any}
            loadingDetails={loadingDetails}
            setSelectedVin={setSelectedVin}
            setSelectedInspection={setSelectedInspection as any}
            setTimelineVin={setTimelineVin}
            shellGameAlerts={shellGameAlerts as any}
            checkingShellGame={checkingShellGame}
            hasCheckedShellGame={hasCheckedShellGame}
            loadCarrier={loadCarrier}
            checkShellGame={checkShellGame as any}
          />
        </div>
      )}

      {/* VIN Detail Modal */}
      {selectedVin && (
        <VINDetailModal
          vin={selectedVin}
          onClose={() => setSelectedVin(null)}
        />
      )}

      {/* Shell Game Timeline Modal */}
      {timelineVin && (
        <ShellGameTimelineModal
          vin={timelineVin}
          onClose={() => setTimelineVin(null)}
          onNavigateToCarrier={(dot) => {
            setTimelineVin(null);
            setSearchedDot(dot);
            loadCarrier(dot);
          }}
          onViewInspection={(inspection) => {
            setSelectedInspection(inspection as any);
          }}
        />
      )}

      {/* Carrier Change History Modal */}
      {showChangeHistoryModal && searchedDot && (
        changeHistory ? (
          <CarrierChangeTimeline
            dotNumber={searchedDot}
            changes={(changeHistory as any).changes || []}
            changesByCategory={(changeHistory as any).changesByCategory || {}}
            riskAssessment={(changeHistory as any).riskAssessment || { hasFrequentChanges: false, hasLocationChanges: false, hasOfficerChanges: false, hasBulkChanges: false, riskLevel: 'LOW' }}
            summary={(changeHistory as any).summary || { totalChanges: 0, suspiciousChanges: 0, significantChanges: 0, firstChangeDate: null, lastChangeDate: null }}
            historicStats={historicStats as any}
            onClose={() => setShowChangeHistoryModal(false)}
          />
        ) : (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }} onClick={() => setShowChangeHistoryModal(false)}>
            <div style={{
              background: 'white',
              padding: '40px',
              borderRadius: '12px',
              textAlign: 'center'
            }} onClick={(e) => e.stopPropagation()}>
              <div style={{ fontSize: '1.5rem', marginBottom: '10px' }}>Loading Change History...</div>
              <div style={{ color: '#666' }}>Please wait</div>
            </div>
          </div>
        )
      )}

      {/* Inspection Details Modal */}
      {selectedInspection && (
        <InspectionDetailsModal
          inspection={selectedInspection}
          onClose={() => setSelectedInspection(null)}
          dotNumber={searchedDot || undefined}
        />
      )}
    </div>
  );
};

export default CarrierDashboard;
